import React, { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { SectionCard } from '../sections/SectionCard';
import { PanelTable } from '../display/PanelTable';
import { SendInvoiceReceiptDialog } from '@features/payments/operations/send-receipt';
import { ReceiptDetailSheet } from './ReceiptDetailSheet';
import type { PanelTableColumn } from '../display/PanelTable/types';
import type { TableAction } from '@shared/types/data-table';
import { DynamicDateCell } from '@shared/lib/tables/cells';
import type { Receipt } from '../../types/panel-common.types';
import type { Payment } from '@shared/types/payment.types';
import { RECEIPTS as PAYMENT_RECEIPTS } from '@shared/data';

interface ReceiptsSectionProps {
  payment?: Payment;
  receiptsData?: Receipt[];
  isDetailView?: boolean;
  title?: string;
  className?: string;
}

// ── Module-level render functions ─────────────────────────────────────────

const renderName = (value: unknown) => {
  const name = String(value);
  return (
    <div className="min-w-0">
      <span className="text-sm text-foreground truncate">{name}</span>
    </div>
  );
};

const renderTimestamp = (value: unknown) => (
  <DynamicDateCell timestamp={String(value)} />
);

const COLUMNS: PanelTableColumn<Receipt>[] = [
  {
    key: 'name',
    label: 'NAME',
    headerClassName: 'w-full',
    cellClassName: 'whitespace-normal',
    render: renderName,
  },
  {
    key: 'timestamp',
    label: 'TIMESTAMP',
    headerClassName: 'w-[150px]',
    cellClassName: 'w-[150px] text-right',
    render: renderTimestamp,
  },
];

// ── Component ─────────────────────────────────────────────────────────────

export const ReceiptsSection = React.memo<ReceiptsSectionProps>(({
  payment,
  receiptsData,
  isDetailView = false,
  title = 'Receipts',
  className,
}) => {
  const receipts = receiptsData ?? PAYMENT_RECEIPTS;
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [viewSheetOpen, setViewSheetOpen] = useState(false);
  const [viewReceipt, setViewReceipt] = useState<Receipt | null>(null);

  const openReceipt = useCallback((receipt: Receipt) => {
    setViewReceipt(receipt);
    setViewSheetOpen(true);
  }, []);

  const rowActions = useCallback((receipt: Receipt): TableAction<Receipt>[] => {
    const actions: TableAction<Receipt>[] = [
      {
        key: 'view',
        label: 'View receipt',
        onClick: () => openReceipt(receipt),
      },
      {
        key: 'download',
        label: 'Download',
        onClick: () => {
          toast.info('PDF download is not yet implemented');
        },
      },
    ];

    if (payment) {
      actions.push({
        key: 'send',
        label: 'Send',
        onClick: () => {
          setSendDialogOpen(true);
        },
      });
    }

    return actions;
  }, [payment, openReceipt]);

  return (
    <>
      <SectionCard
        title={title}
        layout="down"
        className={className}
      >
        <PanelTable
          data={receipts}
          columns={COLUMNS}
          variant={isDetailView ? 'full' : 'panel'}
          emptyMessage="No receipts available"
          onRowClick={openReceipt}
          rowActions={rowActions}
          hideHeaders
          showPagination={false}
          className="[&_thead]:bg-transparent [&_tbody]:bg-transparent"
        />
      </SectionCard>

      {payment && (
        <SendInvoiceReceiptDialog
          open={sendDialogOpen}
          payment={payment}
          onClose={() => setSendDialogOpen(false)}
        />
      )}

      {viewReceipt && (
        <ReceiptDetailSheet
          open={viewSheetOpen}
          receipt={viewReceipt}
          onClose={() => {
            setViewSheetOpen(false);
            setViewReceipt(null);
          }}
        />
      )}
    </>
  );
});

ReceiptsSection.displayName = 'ReceiptsSection';
